import React from 'react';
import { useMemo } from 'react';
import { useQuery, useResponsive } from '@common-fe/common-fe';
import { Box, costFormater, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { DATE_OF_MONTH_FORMAT, DEFAULT_DATE_FORMAT, RAW_DATE_FORMAT } from '@/common/constants';
import { INVESTMENT_PERFORMANCE_LIST } from '@/common/paths';

const QUERY_KEY = 'GET_PERFORMANCE';

interface Response {
  account_balance: number;
  performance_date: string;
}
interface Params {
  daysBack: number;
}

const MOBILE_COLUMNS = 4;
const SMALL_MOBILE_COLUMNS = 3;

const D7 = 7;
const D30 = 30;
const D180 = 180;
const ZONES = 6;
const SMALL_ZONE = 5;
const LARGE_ZONE = 7;
const useGetPerformanceHistoryQuery = ({ daysBack }: Params) => {
  const { isMobile } = useResponsive();
  const { isLoading, data, isSuccess } = useQuery([QUERY_KEY, daysBack], () =>
    // TODO: type is not exist
    api.get<Response[]>(INVESTMENT_PERFORMANCE_LIST, {
      params: {
        days_back: daysBack,
      },
    })
  );

  const values = useMemo(() => {
    const list = data?.data || [];
    return list.map((item) => item.account_balance);
  }, [data?.data]);

  const hints = useMemo(() => {
    const list = data?.data || [];
    return list.map((item) => {
      const date = dayjs(item.performance_date, RAW_DATE_FORMAT);
      return (
        <Box
          round="container2Round"
          key={date.format(DEFAULT_DATE_FORMAT)}
          background="background3"
          pad={{ vertical: 'xxxs', horizontal: 'xs' }}
        >
          <Text color="textOnColor" size="small" weight={400}>
            {date.format(DATE_OF_MONTH_FORMAT)}
          </Text>
          <Text color="textOnColor" size="small" weight={700}>
            {costFormater(item.account_balance, true)}
          </Text>
        </Box>
      );
    });
  }, [data?.data]);
  const valueHints = useMemo(() => {
    const list = data?.data || [];
    return list.map((item) => {
      return costFormater(item.account_balance, true);
    });
  }, [data?.data]);

  const labels = useMemo(() => {
    const list = data?.data || [];
    const labelsList = list.map((item) => dayjs(item.performance_date, RAW_DATE_FORMAT));
    const len = labelsList.length || 0;

    let parts = ZONES;
    if (isMobile) {
      parts = daysBack === 7 ? SMALL_MOBILE_COLUMNS : MOBILE_COLUMNS;
    } else {
      if (daysBack === D7) {
        parts = LARGE_ZONE;
      }
      if (daysBack === D30) {
        parts = SMALL_ZONE;
      }
      if (daysBack === D180) {
        parts = LARGE_ZONE;
      }
    }

    const visibleIndices = Array.from({ length: parts }, (_, i) => Math.floor((i * (len - 1)) / (parts - 1)));
    const labels = labelsList.map((item, index) => {
      if (visibleIndices.includes(index)) {
        return item.format(DATE_OF_MONTH_FORMAT);
      }
      return null;
    });
    return labels;
  }, [data?.data, daysBack, isMobile]);
  return {
    labels,
    hints,
    values,
    valueHints,
    isLoading,
    isSuccess,
  };
};

export default useGetPerformanceHistoryQuery;
